import {
  CONDITIONAL_MARKER_MEANING,
  CONDITIONAL_PREFIX,
  reportedBand,
  SEVERITIES,
  SEVERITY_LIMIT,
  type Severity,
} from '@zevik/schemas';

/**
 * The words this workspace puts in front of a reviewer
 * (docs/DECISIONS.md ZD-112).
 *
 * ## What is imported and why it is not copied
 *
 * `reportedBand`, `CONDITIONAL_PREFIX`, `CONDITIONAL_MARKER_MEANING` and
 * `SEVERITY_LIMIT` are the report's own, shared through `@zevik/schemas`. A
 * reviewer told `medium` where the report says `medium if confirmed` is reviewing
 * a different object, so these must not be written twice — and the whole reason
 * they live in `@zevik/schemas` rather than in `@zevik/report` is that importing
 * the latter into a browser drags the trade-secret packs with it. That
 * measurement is in `client-language.ts`; what matters here is that this file
 * **restates none of it**.
 *
 * ## What this file adds, and the boundary
 *
 * Only the locating clause — where *this* surface keeps the question a
 * conditional finding is waiting on. The report says *"the question is in the
 * next section"* because it is a document with sections; this says where a screen
 * keeps it. That is the one half of the marker's explanation that may differ, and
 * it is why the shared constant stops short of it.
 */

/**
 * The marker's explanation, with this surface's locating clause.
 *
 * Composed rather than stored, so the shared half cannot drift out of it: if
 * `CONDITIONAL_MARKER_MEANING` changes, this sentence changes with it.
 */
export const CONDITIONAL_EXPLANATION =
  `A finding marked ${CONDITIONAL_PREFIX} ${CONDITIONAL_MARKER_MEANING}, and the question is ` +
  `listed under the finding itself.`;

export { CONDITIONAL_PREFIX, reportedBand, SEVERITY_LIMIT };

/**
 * Strongest first, which is the order a reviewer works in.
 *
 * `SEVERITIES` is already declared strongest-first in `@zevik/schemas`, so this
 * is that order read rather than a second one written. Spelled as an index map
 * rather than a comparator over the array so a band the enum gains has a defined
 * position the day it is added instead of sorting to the front.
 */
export const BAND_RANK: Readonly<Record<Severity, number>> = Object.fromEntries(
  SEVERITIES.map((band, index) => [band, index]),
) as Record<Severity, number>;

/**
 * What a reviewer is told this workspace cannot do — **declared, not omitted**.
 *
 * ZD-111 §2 derived fourteen actions a reviewer needs. `sds-12b` served the
 * read-and-decide subset and declared four absences; `sds-12o` closed two of
 * them — the gate and the report are on this screen, and a rewrite reaches
 * the finding, the console and the report (ZD-125) — and the two below are
 * absent for reasons that are recorded rather than accidental. A screen that
 * simply lacks a control leaves a reviewer guessing whether they missed it;
 * ZD-035 §2's rule is that an absence has to be a declaration, and a bare one
 * is an error. So the workspace says so, in the same spirit the report
 * declares its own limits.
 */
export interface Absence {
  what: string;
  why: string;
}

export const ABSENCES: readonly Absence[] = [
  {
    what: 'Answer a client question, or complete an assessment with a deferred finding',
    why:
      'Deferring is the right decision for a finding that rests on something only the client ' +
      'can tell us, and it costs the gate: the assessment cannot be approved while any finding ' +
      'is deferred, because a deferral is a question nobody has answered yet. Asking for the ' +
      'gate names every deferred finding, by title, so you can see what is holding it. The way ' +
      'through exists — open a clarification round, record the client’s answers, re-run the ' +
      'analysis and return here — but none of it is on this screen yet, so a deferral made ' +
      'here cannot be discharged here.',
  },
  {
    what: 'See the recommended control',
    why:
      'The control catalogue has not yet been declared field by field for who may read each ' +
      'part, and some of its text is written for us rather than for a client. Until that is ' +
      'settled a control panel could only show an identifier, which is not something anybody ' +
      'can act on.',
  },
];
